import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  Box,
  IconButton,
  Typography,
  InputAdornment,
  DialogTitle,
} from '@mui/material';
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import { FiX, FiTrash2, FiPlus } from 'react-icons/fi';

const ImageUploadSection = ({ title, images, onImagesChange, maxImages = 5 }) => {
  const [draggedItem, setDraggedItem] = useState(null);

  const handleFileChange = (event) => {
    const newFiles = Array.from(event.target.files);
    if (images.length + newFiles.length > maxImages) {
      alert(`Maximum ${maxImages} images allowed`);
      return;
    }
    onImagesChange([...images, ...newFiles]);
  };

  const handleRemoveImage = (index) => {
    const newImages = [...images];
    newImages.splice(index, 1);
    onImagesChange(newImages);
  };

  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
    e.target.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    setDraggedItem(null);
    e.target.style.opacity = '1';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, index) => {
    e.preventDefault();
    if (draggedItem === null || draggedItem === index) return;
    const newImages = [...images];
    const draggedImage = newImages[draggedItem];
    newImages.splice(draggedItem, 1);
    newImages.splice(index, 0, draggedImage);
    onImagesChange(newImages);
  };

  return (
    <VuiBox mb={3}>
      <VuiTypography variant="button" color="text" fontWeight="bold" mb={1}>
        {title}
      </VuiTypography>
      <VuiBox
        sx={{
          background: 'rgba(255,255,255,0.02)',
          borderRadius: '12px',
          p: 2,
        }}
      >
        <Grid container spacing={2}>
          {images.map((image, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Box
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragEnd={handleDragEnd}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, index)}
                sx={{
                  position: 'relative',
                  width: '100%',
                  paddingTop: '100%',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  border: '2px solid #353570',
                  cursor: 'move',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  },
                  '&:active': {
                    cursor: 'grabbing',
                  },
                }}
              >
                <img
                  src={typeof image === 'string' ? image : URL.createObjectURL(image)}
                  alt={`${title} ${index + 1}`}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    pointerEvents: 'none',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    display: 'flex',
                    gap: 1,
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveImage(index)}
                    sx={{
                      color: 'white',
                      width: 28,
                      height: 28,
                      background: 'rgba(229,57,53,0.9)',
                      '&:hover': {
                        background: 'rgba(229,57,53,1)',
                      },
                    }}
                  >
                    <FiTrash2 size={14} />
                  </IconButton>
                </Box>
              </Box>
            </Grid>
          ))}
          {images.length < maxImages && (
            <Grid item xs={12} sm={6} md={4}>
              <Box
                component="label"
                sx={{
                  width: '100%',
                  paddingTop: '100%',
                  borderRadius: '12px',
                  border: '2px dashed #353570',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  position: 'relative',
                  '&:hover': {
                    borderColor: '#2196f3',
                  },
                }}
              >
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
                <FiPlus
                  size={32}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    color: '#353570',
                  }}
                />
              </Box>
            </Grid>
          )}
        </Grid>
      </VuiBox>
    </VuiBox>
  );
};

const CarEditModal = ({ open, onClose, car, onSave }) => {
  const [formData, setFormData] = useState({
    model: '',
    manufacturer: '',
    year: '',
    color: '',
    plate_number: '',
    fuel_consumption_per_100Km: '',
    identification_marks: '',
    images: [],
  });

  useEffect(() => {
    if (car) {
      setFormData({
        model: car.model || '',
        manufacturer: car.manufacturer || '',
        year: car.year || '',
        color: car.color || '',
        plate_number: car.plate_number || '',
        fuel_consumption_per_100Km: car.fuel_consumption_per_100Km || '',
        identification_marks: car.identification_marks || '',
        images: car.images || [],
      });
    }
  }, [car]);

  const handleInputChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleImagesChange = (newImages) => {
    setFormData((prev) => ({
      ...prev,
      images: newImages,
    }));
  };

  // Convert image URL to File (for existing images)
  const convertImageToFile = async (imageUrl) => {
    try {
      if (imageUrl instanceof File) {
        return imageUrl;
      }
      if (typeof imageUrl === 'string') {
        // Try to extract the path after /storage/
        const storagePath = imageUrl.split('/storage/')[1];
        if (!storagePath) throw new Error('Invalid image URL format');
        // Use /image/ endpoint to fetch
        const newImageUrl = `https://api.csc-taxi.tech/image/${storagePath}`;
        const response = await fetch(newImageUrl);
        if (!response.ok) throw new Error(`Failed to fetch image: ${response.statusText}`);
        const blob = await response.blob();
        const originalFilename = imageUrl.split('/').pop().split('?')[0];
        const filename = originalFilename || `image_${Date.now()}.jpg`;
        return new File([blob], filename, { type: blob.type || 'image/jpeg' });
      }
      return null;
    } catch (error) {
      console.error('Error converting image to file:', { url: imageUrl, error: error.message });
      throw error;
    }
  };

  const handleSubmit = async () => {
    try {
      const formDataToSend = new FormData();
      // Add text fields
      Object.keys(formData).forEach((key) => {
        if (key !== 'images') {
          formDataToSend.append(key, formData[key]);
        }
      });
      // Convert all images to files (existing and new)
      const imageFiles = await Promise.all(
        formData.images.map(async (img) => {
          try {
            const file = await convertImageToFile(img);
            if (!file) throw new Error('Failed to convert image');
            return file;
          } catch (error) {
            console.error('Error converting car image:', { img, error: error.message });
            throw error;
          }
        })
      );
      imageFiles.forEach((file) => {
        if (file instanceof File) {
          formDataToSend.append('images[]', file);
        }
      });
      await onSave(formDataToSend);
      onClose();
    } catch (error) {
      console.error('Error saving car:', error);
      // Optionally show error to user
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          background: '#191932',
          color: '#fff',
          borderRadius: '18px',
          boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
        },
      }}
    >
      <VuiBox
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        px={4}
        pt={4}
        pb={2}
        sx={{
          borderBottom: '1px solid rgba(255,255,255,0.07)',
          background: 'linear-gradient(90deg, #22223a 60%, #1a1a2e 100%)',
          borderTopLeftRadius: '18px',
          borderTopRightRadius: '18px',
        }}
      >
        <VuiTypography variant="h4" color="white" fontWeight="bold">
          Edit Car
        </VuiTypography>
        <IconButton
          onClick={onClose}
          sx={{
            color: '#fff',
            '&:hover': {
              background: 'rgba(255,255,255,0.1)',
            },
          }}
        >
          <FiX size={24} />
        </IconButton>
      </VuiBox>
      <DialogContent sx={{ px: 4, pt: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Model"
              value={formData.model}
              onChange={handleInputChange('model')}
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: '#fff',
                  '& fieldset': {
                    borderColor: 'rgba(255,255,255,0.2)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255,255,255,0.3)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#2196f3',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255,255,255,0.7)',
                  position: 'static',
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Manufacturer"
              value={formData.manufacturer}
              onChange={handleInputChange('manufacturer')}
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: '#fff',
                  '& fieldset': {
                    borderColor: 'rgba(255,255,255,0.2)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255,255,255,0.3)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#2196f3',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255,255,255,0.7)',
                  position: 'static',
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Year"
              value={formData.year}
              onChange={handleInputChange('year')}
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: '#fff',
                  '& fieldset': {
                    borderColor: 'rgba(255,255,255,0.2)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255,255,255,0.3)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#2196f3',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255,255,255,0.7)',
                  position: 'static',
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Color"
              value={formData.color}
              onChange={handleInputChange('color')}
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: '#fff',
                  '& fieldset': {
                    borderColor: 'rgba(255,255,255,0.2)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255,255,255,0.3)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#2196f3',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255,255,255,0.7)',
                  position: 'static',
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Plate Number"
              value={formData.plate_number}
              onChange={handleInputChange('plate_number')}
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: '#fff',
                  '& fieldset': {
                    borderColor: 'rgba(255,255,255,0.2)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255,255,255,0.3)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#2196f3',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255,255,255,0.7)',
                  position: 'static',
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Fuel Consumption (L/100Km)"
              value={formData.fuel_consumption_per_100Km}
              onChange={handleInputChange('fuel_consumption_per_100Km')}
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: '#fff',
                  '& fieldset': {
                    borderColor: 'rgba(255,255,255,0.2)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255,255,255,0.3)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#2196f3',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255,255,255,0.7)',
                  position: 'static',
                },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Identification Marks"
              value={formData.identification_marks}
              onChange={handleInputChange('identification_marks')}
              variant="outlined"
              multiline
              minRows={2}
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: '#fff',
                  '& fieldset': {
                    borderColor: 'rgba(255,255,255,0.2)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255,255,255,0.3)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#2196f3',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255,255,255,0.7)',
                  position: 'static',
                },
              }}
            />
          </Grid>
        </Grid>
        <ImageUploadSection
          title="Car Images"
          images={formData.images}
          onImagesChange={handleImagesChange}
          maxImages={5}
        />
      </DialogContent>
      <DialogActions sx={{ px: 4, pb: 3 }}>
        <Button
          onClick={onClose}
          sx={{
            color: 'rgba(255,255,255,0.7)',
            '&:hover': {
              background: 'rgba(255,255,255,0.1)',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          sx={{
            background: '#2196f3',
            color: '#fff',
            fontWeight: 'bold',
            '&:hover': {
              background: '#1976d2',
            },
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CarEditModal; 